// ============================================================
// BRAND TOKENS — shared by web (frontend/) and mobile (mobile/).
// Ported from frontend/src/constants/brand.js.
//
// NOTE: `shadow` / `shadowLg` are CSS box-shadow strings used by the web app.
// React Native does not understand CSS shadow strings — use the `elevation`
// helpers below (or platform-specific shadow props) on mobile instead.
//
// The base palette hex values below are STABLE — mobile depends on them, so we
// never repaint them. The 2024 web UI refresh layered *additive* tokens on top
// (semantic surfaces, an elevation scale, font families, an extended type
// scale, and font weights). Mobile can ignore the web-only string tokens.
// ============================================================

export const B = {
  navy: "#234c5a",
  navyDk: "#1a3844",
  navyLt: "#2d5f70",
  teal: "#b4dad2",
  tealLt: "#d4ece8",
  tealDk: "#8bbfb5",
  cream: "#f0e7da",
  creamLt: "#f8f3ed",
  creamDk: "#e0d3c2",
  white: "#ffffff",
  text: "#1a2e35",
  textMd: "#4a6572",
  textLt: "#7a9aaa",
  border: "#dde8e5",
  red: "#c0392b",
  green: "#1e7d5a",
  amber: "#c47d1a",
  grey: "#6b7280",
  gold: "#C9A84C",

  // ---- Semantic surfaces (web refresh; additive) ----------------------------
  // A cleaner, cooler canvas than the heavy beige `cream` for the app chrome,
  // while keeping cards crisp white and preserving cream as a warm accent.
  appBg: "#eef2f1",       // page background (replaces raw `cream` on web chrome)
  surface: "#ffffff",     // cards, modals, menus
  surfaceMuted: "#f4f7f6", // insets, table headers, hover fills
  surfaceSunken: "#e8eeec", // wells, track backgrounds
  hover: "#f1f5f4",       // row / control hover
  divider: "#e4ecea",     // hairline separators (slightly cooler than `border`)
  overlay: "rgba(22,42,50,0.55)", // modal backdrop

  // Web-only CSS box-shadow strings (ignored by React Native).
  // `shadow` / `shadowLg` kept at their original values for back-compat.
  shadow: "0 2px 12px rgba(35,76,90,0.10)",
  shadowLg: "0 8px 32px rgba(35,76,90,0.15)",
  // Layered elevation scale (softer, more modern than a single flat shadow).
  shadowXs: "0 1px 2px rgba(26,46,53,0.06)",
  shadowSm: "0 1px 2px rgba(26,46,53,0.06), 0 1px 3px rgba(26,46,53,0.08)",
  shadowMd: "0 2px 6px rgba(26,46,53,0.06), 0 6px 20px rgba(26,46,53,0.08)",
  shadowXl: "0 16px 48px rgba(26,46,53,0.18)",
  // Focus ring (used by inputs/controls for an inset-friendly glow).
  ring: "0 0 0 3px rgba(35,76,90,0.18)",

  // ---- Web refresh v2 (Fluent / Outlook-iOS inspired; additive, web-only) ---
  // A cooler, more confident accent in the same navy-teal family, used for
  // links, focus rings, and active accents so the UI reads fresher and crisper.
  accent: "#2b7a8c",
  accentDk: "#215f6e",
  accentLt: "#e6f3f4",
  // Subtle gradients give chrome and primary controls a soft sense of depth
  // (the modern, layered look) without leaving the brand palette.
  gradSidebar: "linear-gradient(180deg, #204350 0%, #142c36 100%)",
  gradPrimary: "linear-gradient(180deg, #2a5a6b 0%, #21444f 100%)",
  gradBrand: "linear-gradient(135deg, #234c5a 0%, #2d5f70 55%, #367486 100%)",
  gradTeal: "linear-gradient(135deg, #b4dad2 0%, #8bbfb5 100%)",
  // Frosted glass surfaces (headers, floating bars) — pair with backdrop-blur.
  glass: "rgba(255,255,255,0.72)",
  glassBorder: "rgba(255,255,255,0.55)",
  // Hover elevation for interactive cards (a gentle lift, not a hard shadow).
  shadowLift: "0 12px 32px rgba(26,46,53,0.14), 0 2px 8px rgba(26,46,53,0.06)",
  // Accent-tinted focus ring (cooler than the navy `ring`).
  ringAccent: "0 0 0 3px rgba(43,122,140,0.22)",
} as const;

export type BrandColor = keyof typeof B;

// ============================================================
// NUMERIC SCALES — spacing, type, and radius tokens.
// Plain numbers (no units) so both web (px via inline styles) and
// mobile (RN density-independent units) can consume them.
// ============================================================

export const space = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  xxl: 24,
  xxxl: 40,
} as const;

export const font = {
  xs: 11,
  sm: 12,
  base: 13,
  md: 14,
  lg: 16,
  xl: 20,
  xxl: 28,
  // Extended display sizes (web refresh; additive).
  xxxl: 34,
  display: 44,
} as const;

export const radius = {
  sm: 4,
  md: 6,
  lg: 8,
  xl: 10,
  xxl: 12,
  // Larger, friendlier corners for cards, modals, and buttons (web refresh v2).
  xl2: 14,
  xxxl: 16,
  pill: 20,
} as const;

// ============================================================
// TYPOGRAPHY — font-family stacks and weights (web refresh; additive).
// `fonts.sans` is the app default (Inter, self-hosted via @fontsource).
// Mobile can ignore these strings and use its own platform fonts.
// ============================================================

export const fonts = {
  sans: '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  mono: '"SFMono-Regular", ui-monospace, Menlo, Consolas, "Liberation Mono", monospace',
  serif: 'Georgia, "Times New Roman", serif',
} as const;

export const weight = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;
